import type {
  CompensationRange,
  EmploymentType,
  SeniorityLevel,
  WorkArrangement,
  WorkAuthorization,
} from './core';

/**
 * Opportunity verification vocabulary. This is deliberately SEPARATE from
 * client disclosure: an opportunity can be confidential AND verified, or
 * disclosed AND merely proposed. Confidentiality must never be able to imply
 * that an unconfirmed role is a live vacancy.
 */
export type OpportunityStatus =
  | 'verified'
  | 'confidential_mandate'
  | 'proposed_opportunity'
  | 'market_matched_opportunity';

export type ClientDisclosure = 'disclosed' | 'withheld_at_client_request';

export type OpportunityLifecycle = 'draft' | 'pending_verification' | 'open' | 'on_hold' | 'closed' | 'filled';

/** The exact wording required whenever a client's identity is withheld. */
export const CONFIDENTIAL_MANDATE_NOTICE =
  'This search is being conducted confidentially at the client’s request. Qualified candidates who advance through the preliminary review will receive additional organizational information at the appropriate stage and before their profile is presented. No candidate information will be shared without prior authorization.';

export const PROPOSED_OPPORTUNITY_NOTICE =
  'No active vacancy is confirmed for this opportunity. It reflects a leadership requirement under discussion or identified through market research. Registering interest records your consent to be considered should a formal mandate be commissioned.';

export const EQUAL_OPPORTUNITY_STATEMENT =
  'Our clients are equal-opportunity employers. Consideration is given without regard to race, color, religion, sex, sexual orientation, gender identity, national origin, disability, veteran status, age, or any other characteristic protected by applicable law. Reasonable accommodations are available at any stage of the process on request.';

/** Entity: Opportunities */
export interface Opportunity {
  id: string;
  referenceNumber: string;
  slug: string;
  title: string;
  status: OpportunityStatus;
  lifecycle: OpportunityLifecycle;
  clientDisclosure: ClientDisclosure;
  /** Only rendered publicly when clientDisclosure === 'disclosed'. */
  disclosedClientName?: string;
  /** Descriptive stand-in used when the client is withheld. */
  clientDescriptor: string;
  mandateId?: string;
  industry: string;
  function: string;
  location: string;
  region: 'united_states' | 'emea' | 'apac' | 'americas_ex_us';
  workArrangement: WorkArrangement;
  employmentType: EmploymentType;
  seniority: SeniorityLevel;
  compensation: CompensationRange;
  reportingLine: string;
  roleOverview: string;
  organizationalContext: string;
  keyResponsibilities: string[];
  requiredQualifications: string[];
  preferredQualifications: string[];
  leadershipScope: {
    teamSize?: string;
    budget?: string;
    pAndLOwnership: boolean;
    boardExposure: boolean;
    directReports?: string;
  };
  benefits: string[];
  requiredSkills: string[];
  educationRequirement?: string;
  licensingRequirement?: string;
  workAuthorizationRequirement: WorkAuthorization[];
  screeningQuestions: ScreeningQuestion[];
  applicationDeadline?: string;
  postedAt: string;
  updatedAt: string;
  ownerRecruiterId: string;
  /** Internal only — never serialised to a public view. */
  internal: OpportunityInternalRecord;
}

/** Administrator-only verification evidence. Never exposed to public or candidate views. */
export interface OpportunityInternalRecord {
  actualClientName: string;
  mandateOwnerUserId: string;
  verificationEvidence: string;
  verifiedByUserId?: string;
  verificationDate?: string;
  vacancyStatus: 'confirmed_open' | 'anticipated' | 'exploratory' | 'closed';
  clientAuthorizationDate?: string;
}

export interface ScreeningQuestion {
  id: string;
  prompt: string;
  type: 'short_text' | 'long_text' | 'single_select' | 'boolean';
  options?: string[];
  required: boolean;
}

export type MandateStage =
  | 'inquiry_received'
  | 'scoping'
  | 'engaged'
  | 'research_and_mapping'
  | 'longlist'
  | 'shortlist'
  | 'client_interviews'
  | 'final_stage'
  | 'offer'
  | 'placed'
  | 'closed_unfilled';

export type ConfidentialityLevel = 'public' | 'confidential' | 'strictly_confidential';

/** Entity: Search mandates */
export interface SearchMandate {
  id: string;
  reference: string;
  employerProfileId: string;
  positionTitle: string;
  reportingLine: string;
  industry: string;
  location: string;
  workArrangement: WorkArrangement;
  compensationRange: CompensationRange;
  leadershipResponsibilities: string;
  requiredExperience: string;
  urgency: 'immediate' | 'within_quarter' | 'within_half_year' | 'planning';
  confidentialityLevel: ConfidentialityLevel;
  reasonForHiring: 'growth' | 'replacement' | 'newly_created' | 'succession' | 'transformation';
  isReplacementSearch: boolean;
  replacementIncumbentStatus?: 'in_seat' | 'departed' | 'notified' | 'not_yet_notified';
  engagementType: 'retained_search' | 'confidential_replacement' | 'market_mapping' | 'interim_appointment' | 'advisory';
  specificationDocumentName?: string;
  stage: MandateStage;
  ownerRecruiterId: string;
  opportunityId?: string;
  createdAt: string;
  updatedAt: string;
}

export type ApplicationStatus =
  | 'draft'
  | 'submitted'
  | 'under_preliminary_review'
  | 'additional_information_requested'
  | 'search_team_review'
  | 'client_authorized_submission_pending'
  | 'candidate_consent_required'
  | 'presented_to_client'
  | 'client_review'
  | 'interview_stage'
  | 'on_hold'
  | 'not_selected'
  | 'withdrawn'
  | 'offer_stage'
  | 'search_completed';

export interface ApplicationEvent {
  id: string;
  status: ApplicationStatus;
  occurredAt: string;
  actorUserId: string;
  actorRoleLabel: string;
  note: string;
  candidateVisible: boolean;
}

/** Entity: Applications */
export interface Application {
  id: string;
  reference: string;
  opportunityId: string;
  candidateProfileId: string;
  status: ApplicationStatus;
  submittedAt?: string;
  resumeDocumentId?: string;
  screeningAnswers: { questionId: string; answer: string }[];
  compensationAlignmentConfirmed: boolean;
  locationAlignmentConfirmed: boolean;
  internalReviewConsentAt?: string;
  events: ApplicationEvent[];
  /** Required before status may become 'not_selected'. */
  adverseDecision?: {
    reviewerUserId: string;
    reviewerName: string;
    reason: string;
    recordedAt: string;
  };
  createdAt: string;
  updatedAt: string;
}

/** Entity: Saved opportunities */
export interface SavedOpportunity {
  id: string;
  candidateProfileId: string;
  opportunityId: string;
  savedAt: string;
}

export type MatchDimension =
  | 'seniority'
  | 'industry_experience'
  | 'functional_expertise'
  | 'leadership_scope'
  | 'location'
  | 'work_arrangement'
  | 'compensation'
  | 'work_authorization'
  | 'education_licensing'
  | 'required_skills';

export interface MatchFactor {
  dimension: MatchDimension;
  label: string;
  weight: number;
  /** 0–1, or null when the candidate has not supplied the data. */
  score: number | null;
  outcome: 'contributed' | 'conflicted' | 'insufficient_data';
  explanation: string;
}

/** Entity: Candidate matches */
export interface CandidateMatch {
  id: string;
  candidateProfileId: string;
  opportunityId: string;
  /** 0–100 composite. Recruiter-visible only. */
  score: number;
  /** Candidate-visible band. Never a guarantee of qualification. */
  band: 'potential_match' | 'strong_potential_match' | 'below_threshold';
  factors: MatchFactor[];
  computedAt: string;
}

export type ConsentType =
  | 'privacy_notice'
  | 'general_candidacy'
  | 'named_employer_presentation'
  | 'confidential_mandate_consideration'
  | 'marketing_communications'
  | 'internal_review'
  | 'mandate_specific_presentation';

/** Entity: Consent records — append-only. */
export interface ConsentRecord {
  id: string;
  candidateProfileId: string;
  type: ConsentType;
  granted: boolean;
  /** Present for mandate-scoped authorizations. */
  mandateId?: string;
  opportunityId?: string;
  employerDisclosedAs?: string;
  disclosureRestrictionReason?: string;
  recordedAt: string;
  recordedVia: 'registration' | 'portal' | 'application' | 'authorization_request' | 'privacy_settings';
  ipNote: string;
  supersedesId?: string;
}

export type PresentationStatus =
  | 'authorization_requested'
  | 'authorized'
  | 'declined_by_candidate'
  | 'presented'
  | 'withdrawn';

/**
 * Entity: Candidate presentations.
 * The ONLY bridge between a candidate and an employer. Employer-side views are
 * derived from these records, never filtered from the candidate table.
 */
export interface CandidatePresentation {
  id: string;
  mandateId: string;
  opportunityId: string;
  candidateProfileId: string;
  applicationId: string;
  employerProfileId: string;
  status: PresentationStatus;
  authorizationConsentId?: string;
  employerNamedInRequest?: string;
  disclosureRestrictionReason?: string;
  requestedAt: string;
  respondedAt?: string;
  presentedAt?: string;
  documentIdsShared: string[];
}

export type InterviewStatus = 'requested' | 'availability_requested' | 'scheduled' | 'completed' | 'cancelled';

/** Entity: Interviews */
export interface Interview {
  id: string;
  presentationId: string;
  mandateId: string;
  candidateProfileId: string;
  employerProfileId: string;
  stageLabel: string;
  format: 'video' | 'in_person' | 'telephone';
  status: InterviewStatus;
  proposedSlots: string[];
  confirmedSlot?: string;
  location?: string;
  panel: string[];
  candidateBriefingNote?: string;
  createdAt: string;
}

/** Entity: Employer feedback */
export interface EmployerFeedback {
  id: string;
  presentationId: string;
  employerProfileId: string;
  authorUserId: string;
  submittedAt: string;
  recommendation: 'advance' | 'hold' | 'decline';
  strengths: string;
  reservations: string;
  fitAgainstSpecification: 1 | 2 | 3 | 4 | 5;
}
